import type { TagProps } from "@wayflyer/flyui";
import { Tag, Flex, useMediaQuery } from "@wayflyer/flyui";
import { styled } from "styled-components";

import type { ConnectorProps } from "../connector-button";
import type { ConnectorWithModal } from "../connector-content/interface";

import { DefaultManagementPanel } from "./panels/default-management-panel";
import { ConnectionContainer, ConnectionListContainer } from "./platform-connection-list.styles";
import { PlatformDetails } from "./platform-details";
import { PlatformImage } from "./platform-image";

type LegacyTag =
  | "notconnected"
  | "disconnected"
  | "connected"
  | "paid"
  | "active"
  | "paused"
  | "green"
  | "red"
  | "purple"
  | "orange";

export interface PlatformConnection {
  name: string;
  domain?: string;
  additionalDetails?: Array<string>;
  logoOverride?: React.ReactNode;
  connectorType: string;
  tagData?: {
    label: string;
    type?: LegacyTag;
    status?: "connected" | "disconnected" | "expiringSoon";
  };
  reconnectionData?: ConnectorWithModal | ConnectorProps;
  panel?: React.ReactNode;
}

export interface ConnectionListProps {
  connections: PlatformConnection[];
}

const groupConnectionsByPlatform = (connections: PlatformConnection[]): [string, PlatformConnection[]][] => {
  const sortedConnections = new Map<string, PlatformConnection[]>();
  connections.forEach((connection) => {
    const connectorTypeStores = sortedConnections.get(connection.connectorType);
    connectorTypeStores
      ? connectorTypeStores.push(connection)
      : sortedConnections.set(connection.connectorType, [connection]);
  });
  return Array.from(sortedConnections);
};

const badgeStatusMap: Record<LegacyTag, TagProps["color"]> = {
  notconnected: undefined,
  disconnected: "critical",
  connected: "positive",
  paid: undefined,
  active: "positive",
  paused: "warning",
  green: "positive",
  red: "critical",
  purple: undefined,
  orange: "warning",
};

const getBadgeStatus = (connection: PlatformConnection) => {
  if (connection.tagData?.type) {
    return badgeStatusMap[connection.tagData.type];
  }
  if (connection.tagData?.status) {
    const status = connection.tagData.status;
    if (status === "connected") {
      return "positive";
    }
    if (status === "expiringSoon") {
      return "warning";
    }

    return "critical";
  }

  return undefined;
};

const Spacer = styled.div`
  width: 32px;
`;

const ConnectionItem = styled.div`
  align-items: flex-start;
  display: flex;
  flex-wrap: nowrap;
`;

const FillItem = styled.div`
  flex: 1;
  align-self: center;
  margin: 15px 0 15px 0;
`;

const AlignWithLogo = styled.div`
  height: 74px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Connection = ({ connection }: { connection: PlatformConnection }) => {
  const query = useMediaQuery();
  const isSmallScreen = query("xs").windowLte;

  const connectionIdentifier = (
    <PlatformDetails
      name={connection.name}
      domain={connection.domain}
      additionalDetails={connection.additionalDetails}
    />
  );

  const tag = connection.tagData && <Tag color={getBadgeStatus(connection)} content={connection.tagData.label} />;
  const managementPanel = connection.panel ? (
    connection.panel
  ) : connection.reconnectionData ? (
    <Spacer>
      <DefaultManagementPanel {...connection} />
    </Spacer>
  ) : (
    <Spacer />
  );

  return (
    <ConnectionContainer>
      {isSmallScreen ? (
        <Flex align="center" wrap="nowrap">
          <Flex wrap="wrap" grow={1}>
            <Flex direction="column">
              <Flex align="center" justify="space-between">
                <PlatformImage connectorType={connection.connectorType} logoOverride={connection.logoOverride} />
                {tag}
              </Flex>
              {connectionIdentifier}
            </Flex>
          </Flex>
          {managementPanel}
        </Flex>
      ) : (
        <ConnectionItem>
          <PlatformImage connectorType={connection.connectorType} logoOverride={connection.logoOverride} />
          <FillItem>{connectionIdentifier}</FillItem>
          <AlignWithLogo>{tag}</AlignWithLogo>
          <AlignWithLogo>{managementPanel}</AlignWithLogo>
        </ConnectionItem>
      )}
    </ConnectionContainer>
  );
};

// PlatformConnectionList for all platforms, should be exported by default
export const PlatformConnectionList = ({ connections }: ConnectionListProps) => {
  const sortedConnections = groupConnectionsByPlatform(connections);

  return (
    <Flex direction="column" gap="4">
      {sortedConnections.map(([type, connections]) => (
        <ConnectionListContainer key={type}>
          {connections.map((connection) => (
            <Connection connection={connection} key={connection.name + connection.domain} />
          ))}
        </ConnectionListContainer>
      ))}
    </Flex>
  );
};
